import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileText, BadgeCheck, ChevronDown } from "lucide-react";

const legalDocs = [
  { title: "Akta Pendirian", date: "16 September 2010", no: "No. 14" },
  { title: "Akta Risalah Saham", date: "29 Juni 2016", no: "No. 54" },
  { title: "Akta Hibah Saham", date: "29 Juni 2016", no: "No. 55" },
  { title: "Akta Perubahan", date: "07 September 2020", no: "No. 09" },
  { title: "Akta Perubahan", date: "29 Agustus 2024", no: "No. 13" },
  { title: "Akta Perubahan Terakhir", date: "29 Oktober 2025", no: "No. 09" },
];

function TimelineItem({ doc, isExpanded, isLatest, onClick, index }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: (i) => ({
          opacity: 1,
          y: 0,
          transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
        }),
      }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={index}
      whileHover={{ scale: 1.01 }}
      className="group relative cursor-pointer"
      onClick={onClick}
    >
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-green-500 to-teal-500 opacity-0 blur-xl transition duration-500 group-hover:opacity-30"></div>

      <div className="relative flex items-start gap-4 md:gap-6 transition-all duration-500 group-hover:scale-[1.02]">
        {/* Icon */}
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className={`z-10 flex h-12 w-12 md:h-16 md:w-16 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-teal-500 shadow-md transition-all duration-300 ${
            isExpanded ? "shadow-green-400/50" : ""
          }`}
        >
          <BadgeCheck className="h-6 w-6 md:h-8 md:w-8 text-white" />
        </motion.div>

        {/* Item content */}
        <div className="relative flex-1 overflow-hidden rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-md backdrop-blur-sm transition-all duration-500 hover:shadow-lg hover:border-green-200">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 md:text-xl">
              {doc.title}
            </h4>

            {isLatest && !isExpanded && (
              <span className="ml-auto mr-2 rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-semibold text-teal-800">
                Terbaru
              </span>
            )}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="h-5 w-5 text-gray-400" />
            </motion.div>
          </div>

          {!isExpanded && (
            <p className="mt-2 font-mono text-sm text-gray-500">{doc.no}</p>
          )}

          <motion.div
            initial={false}
            animate={{
              height: isExpanded ? "auto" : 0,
              opacity: isExpanded ? 1 : 0,
              marginTop: isExpanded ? 16 : 0,
            }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden border-t border-gray-200/80"
          >
            <div className="pt-4 space-y-3">
              {isLatest && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 p-2 text-sm text-teal-800"
                >
                  <div className="h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-teal-500 animate-pulse"></div>
                  <span>Ini adalah dokumen legalitas terbaru kami.</span>
                </motion.div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Tanggal:
                </span>
                <span className="font-mono text-sm text-gray-800">
                  {doc.date}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Nomor:
                </span>
                <span className="font-mono text-sm text-gray-800">
                  {doc.no}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

function LegalityTimeline() {
  const [expandedIndex, setExpandedIndex] = useState(legalDocs.length - 1);
  const handleItemClick = (i) =>
    setExpandedIndex(expandedIndex === i ? null : i);

  return (
    <div className="relative">
      {/* Vertical line */}
      <motion.div
        animate={{ backgroundPosition: ["0% 0%", "0% 100%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-0 left-6 md:left-8 top-0 block w-0.5 bg-gradient-to-b from-green-300 via-teal-400 to-green-500 opacity-40"
      ></motion.div>

      <div className="space-y-8">
        {legalDocs.map((doc, i) => (
          <TimelineItem
            key={i}
            doc={doc}
            index={i}
            isExpanded={expandedIndex === i}
            isLatest={i === legalDocs.length - 1}
            onClick={() => handleItemClick(i)}
          />
        ))}
      </div>
    </div>
  );
}

export default function LegalitasPage() {
  return (
    <main className="relative bg-white py-28 px-6 md:px-16 overflow-hidden">
      {/* Background animasi */}
      <motion.div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-300/10 rounded-full blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.07, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-300/10 rounded-full blur-[120px]"
        ></motion.div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]"></div>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="relative z-10 mx-auto max-w-7xl text-center mb-16"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-200 rounded-full backdrop-blur-sm mb-6">
          <FileText className="w-4 h-4 text-green-600" />
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Dokumen Resmi
          </span>
        </div>

        <motion.h1
          whileHover={{ scale: 1.02 }}
          className="text-5xl md:text-7xl font-black mb-6 tracking-tight"
        >
          <span className="text-slate-900">Legalitas </span>
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-600 via-teal-500 to-green-700">
            Perusahaan
          </span>
          <span className="text-green-600">Perusahaan</span>
        </motion.h1>

        <div className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"></div>

        <p className="max-w-3xl mx-auto text-slate-700 text-lg leading-relaxed font-light">
          Soney Raya beroperasi di bawah payung hukum yang jelas. Berikut adalah
          rincian dokumen resmi yang menjamin{" "}
          <span className="font-semibold text-slate-900">
            validitas operasional
          </span>{" "}
          kami.
        </p>
      </motion.div>

      {/* Timeline langsung tanpa dasar card */}
      <div className="relative z-10 max-w-4xl mx-auto">
        <LegalityTimeline />
      </div>
    </main>
  );
}
